import { notificationsApi, type PushSubscriptionBody } from '../api';

/**
 * Browser side of web push (notifications doc §3.2): service worker registration, PushManager subscription and the
 * API calls that register it. A subscription belongs to one browser profile; `OWNER_KEY` remembers which Tibyan
 * account turned it on here, so a different account signing in on a shared computer never inherits it silently.
 */

export const SW_URL = '/sw.js';
export const OWNER_KEY = 'tibyan.push.owner';

export type PushSupport = 'supported' | 'unsupported' | 'needs-install';
export type EnableResult = 'on' | 'denied' | 'dismissed' | 'unavailable';

function readOwner(): string | null {
  try {
    return globalThis.localStorage?.getItem(OWNER_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeOwner(userId: string | null): void {
  try {
    if (userId) globalThis.localStorage?.setItem(OWNER_KEY, userId);
    else globalThis.localStorage?.removeItem(OWNER_KEY);
  } catch {
    /* storage blocked: the subscription still works, only the shared-computer guard is lost */
  }
}

function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || Boolean(window.matchMedia?.('(display-mode: standalone)').matches);
}

function isIos(): boolean {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** iOS/iPadOS Safari (16.4+) offers push only to a site added to the Home Screen. */
export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  const hasApis = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!hasApis) return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  return 'supported';
}

export function notificationPermission(): NotificationPermission {
  return typeof Notification === 'undefined' ? 'default' : Notification.permission;
}

/** VAPID key (base64url, no padding) → the bytes PushManager.subscribe expects. */
export function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padded = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(sub: PushSubscription, publicKey: string): boolean {
  const current = sub.options?.applicationServerKey;
  if (!current) return true; // not exposed by this browser: trust it, the server revokes a mismatch on send
  const a = new Uint8Array(current);
  const b = urlBase64ToUint8Array(publicKey);
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function toSubscriptionBody(sub: PushSubscription): PushSubscriptionBody {
  const json = sub.toJSON();
  return {
    endpoint: json.endpoint ?? sub.endpoint,
    keys: { p256dh: json.keys?.p256dh ?? '', auth: json.keys?.auth ?? '' },
    expirationTime: json.expirationTime ?? null,
  };
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  } catch {
    return null;
  }
}

async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (!existing) await navigator.serviceWorker.register(SW_URL, { scope: '/' });
  return navigator.serviceWorker.ready; // subscribe() needs an active worker
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (pushSupport() !== 'supported') return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

/** Whether this browser pushes to `userId` right now (permission granted, subscribed, turned on by that account). */
export async function isPushOnFor(userId: string): Promise<boolean> {
  if (pushSupport() !== 'supported' || notificationPermission() !== 'granted') return false;
  return (await currentSubscription()) !== null && readOwner() === userId;
}

async function subscribeWith(publicKey: string): Promise<PushSubscription> {
  const reg = await activeRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, publicKey)) {
    await sub.unsubscribe(); // server key rotated (or a dev restart): the old subscription can no longer be used
    sub = null;
  }
  return sub ?? reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
}

/**
 * Turn browser notifications on. Call it straight from the click handler: the permission prompt must come from a
 * user gesture (browsers ignore or penalise prompts that do not), so it is the first thing this does.
 */
export async function enablePush(publicKey: string, userId: string): Promise<EnableResult> {
  const permission = notificationPermission() === 'granted' ? 'granted' : await Notification.requestPermission();
  if (permission === 'denied') return 'denied';
  if (permission !== 'granted') return 'dismissed';
  const sub = await subscribeWith(publicKey);
  await notificationsApi.pushSubscribe(toSubscriptionBody(sub));
  writeOwner(userId);
  return 'on';
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (sub) {
    try {
      await notificationsApi.pushUnsubscribe(sub.endpoint);
    } finally {
      await sub.unsubscribe().catch(() => false);
    }
  }
  writeOwner(null);
}

/**
 * After sign-in and on every app start: re-register this browser's subscription for its owner (idempotent), and
 * re-subscribe when the server's VAPID key changed. Never claims a subscription another account turned on.
 */
export async function syncPushSubscription(userId: string): Promise<'synced' | 'skipped'> {
  if (pushSupport() !== 'supported' || notificationPermission() !== 'granted' || readOwner() !== userId) return 'skipped';
  const existing = await currentSubscription();
  if (!existing) return 'skipped';
  const { enabled, public_key } = await notificationsApi.pushPublicKey();
  if (!enabled || !public_key) return 'skipped';
  const sub = await subscribeWith(public_key);
  await notificationsApi.pushSubscribe(toSubscriptionBody(sub));
  return 'synced';
}

/** The service worker saw `pushsubscriptionchange`: drop the old endpoint and register the new subscription. */
export async function handleSubscriptionChange(userId: string, oldEndpoint: string | null): Promise<void> {
  if (oldEndpoint) await notificationsApi.pushUnsubscribe(oldEndpoint).catch(() => undefined);
  await syncPushSubscription(userId);
}

/** Sign-out on this browser: stop pushes for that account here (the endpoint dies; the server revokes it on 410). */
export async function releaseDevice(userId: string): Promise<void> {
  if (readOwner() !== userId) return;
  writeOwner(null);
  const sub = await currentSubscription().catch(() => null);
  await sub?.unsubscribe().catch(() => false);
}
